import { ref, computed, type Ref } from "vue";

export type ThemeMode = "system" | "light" | "dark";
export type EffectiveTheme = "light" | "dark";

const STORAGE_KEY = "app-theme-preference";
const DARK_CLASS = "app-dark-mode";

const themeMode: Ref<ThemeMode> = ref("system");
const systemPrefersDark = ref(false);

const effectiveTheme = computed<EffectiveTheme>(() => {
  if (themeMode.value === "system") return systemPrefersDark.value ? "dark" : "light";
  return themeMode.value;
});

let mediaQuery: MediaQueryList | null = null;

function onSystemChange(e: MediaQueryListEvent) {
  systemPrefersDark.value = e.matches;
  applyClass();
}

function applyClass() {
  const isDark = effectiveTheme.value === "dark";
  document.documentElement.classList.toggle(DARK_CLASS, isDark);
}

function startListening() {
  if (mediaQuery) return;
  mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  systemPrefersDark.value = mediaQuery.matches;
  mediaQuery.addEventListener("change", onSystemChange);
}

function stopListening() {
  if (!mediaQuery) return;
  mediaQuery.removeEventListener("change", onSystemChange);
  mediaQuery = null;
}

function setThemeMode(mode: ThemeMode) {
  themeMode.value = mode;

  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Storage disabled or quota exceeded: keep the in-memory preference,
    // it just won't survive a reload.
  }

  if (mode === "system") {
    startListening();
  } else {
    stopListening();
  }
  applyClass();
}

function cycleTheme() {
  const order: ThemeMode[] = ["system", "light", "dark"];
  const next = order[(order.indexOf(themeMode.value) + 1) % order.length] ?? "system";
  setThemeMode(next);
}

let initialized = false;

function initTheme() {
  if (initialized) return;
  initialized = true;

  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage blocked (e.g. privacy mode): fall back to "system" below.
  }

  if (stored === "system" || stored === "light" || stored === "dark") {
    setThemeMode(stored);
  } else {
    setThemeMode("system");
  }
}

export function useTheme() {
  return { themeMode, effectiveTheme, setThemeMode, cycleTheme, initTheme };
}
